import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import MemberDetailsCard from "../../../components/MemberDetailsCard";
import { Badge } from "../../../components/catalyst/badge";
import { getStatusBadgeClass } from "../../../util/statusBadge";
import { Field } from "../../../components/catalyst/fieldset";
import { useToast } from "../../../util/ToastProvider";
import  AddEditNoteDialog  from "../../../components/dialogs/AddEditNoteFromAdminDialog";


async function fetchAllUserReports(userId: string) {
  const fn = httpsCallable(functions, "workflowFetchAllUserReports");
  const result = await fn({ action: userId });
  return result.data as { count: number; data: any[], visits: { month: string; year: number; value: number }[], user: any };
}
;

function InfoRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-slate-900">{children}</div>
    </div>
  );
}





function Card({
  title,
  children,
  actions,
  className = "",
}: {
  title?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm w-full ${className}`}>
      {title && (
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}


function KeyValue({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="text-xs text-slate-500 w-40 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-800">{value}</dd>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold text-slate-900 mb-3">{children}</h2>;
}

export default function MembersViewProfile() {
  const { id: userId } = useParams();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [member, setMember] = useState<any>()
  const total = 900;
  const [visits, setVisits] = useState<any[]>([]);
  const nav = useNavigate();
  const [freqOpened, setFreqOpened] = useState<any[]>([])
  const [showRelatedDialog, setShowRelatedDialog] = useState(false);
  const [openPersonId, setOpenPersonId] = useState<string | null>(null);
  const [openNoteDialog, setOpenNoteDialog] = useState(false);

  const [isTogglingUser, setIsTogglingUser] = useState(false);
  const [isUserDisabled, setIsUserDisabled] = useState<boolean>(false);
  const toast = useToast()

  const relatedPersonsList = React.useMemo(() => {
    if (!member?.relatedPersons) return [];
    return Object.values(member.relatedPersons);
  }, [member]);


  useEffect(() => {
    (async () => {
      setLoading(true);

      const res = await fetchAllUserReports(userId || "");

      // reports now contain ONLY template + report
      setReports(res.data || []);

      const a = res.data.map(v => {
        return { views: v.report.viewCount, title: v.template.title }
      })
      console.log("qwqwqw", a)
      setFreqOpened(a)

      // user info now comes separately
      setMember(res.user || null);
      setIsUserDisabled(Boolean(res.user?.disabled));


      // visits for chart
      setVisits(res.visits || []);

      setLoading(false);
    })();
  }, [userId]);


  const handleToggleAccount = async () => {
    if (!member?.email) return;

    try {
      setIsTogglingUser(true);

      const fn = httpsCallable<
        { type: string; email: string },
        { success: boolean; action: "disabled" | "enabled"; message: string }
      >(functions, "authUserHandler");

      const result = await fn({
        type: "disableUser",
        email: member.email,
      });

      const { action, message } = result.data;

      setIsUserDisabled(action === "disabled");

      toast.success(message || "Account status updated");

    } catch (err: any) {
      const msg =
        err?.message ||
        err?.details ||
        "Failed to update account status";

      toast.error(msg);
    } finally {
      setIsTogglingUser(false);
    }
  };





  // Skeleton Loader
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 p-6 md:p-10">
        <div className="mx-auto max-w-7xl space-y-8">
          {/* Header Skeleton */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-slate-200" />
              <div className="space-y-2">
                <div className="h-4 w-48 bg-slate-200 rounded" />
                <div className="h-3 w-32 bg-slate-200 rounded" />
              </div>
            </div>
            <div className="h-6 w-24 bg-slate-200 rounded" />
          </div>

          {/* Recent Reports Skeleton */}
          <div className="space-y-3">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 animate-pulse"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-7 w-7 rounded-full bg-slate-200" />
                    <div className="space-y-2">
                      <div className="h-3 w-40 bg-slate-200 rounded" />
                      <div className="h-2 w-24 bg-slate-200 rounded" />
                    </div>
                  </div>
                  <div className="h-5 w-16 bg-slate-200 rounded" />
                </div>
              ))}
          </div>

          {/* Details & Purchases Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse space-y-2">
              {Array(6)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="h-4 w-full bg-slate-200 rounded" />
                ))}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 animate-pulse space-y-2">
              {Array(4)
                .fill(0)
                .map((_, i) => (
                  <div key={i} className="h-4 w-full bg-slate-200 rounded" />
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  function formatDOB(dob?: string | null): string {
    if (!dob) return "";

    // Parse manually to avoid timezone shift
    const [year, month, day] = dob.split("-").map(Number);

    if (!year || !month || !day) return dob;

    const date = new Date(year, month - 1, day); // local date, no UTC shift

    return date.toLocaleDateString("en", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  function handleGoToUser() {
    if (!userId) return;

    window.open(`/admin/user-view/${userId}`, '_blank');
  }

  return (
    <div className="min-h-screen bg-slate-50/60 p-6 md:p-10">
      <div className={`mx-auto max-w-7xl space-y-8 ${isTogglingUser} ? "pointer-events-none opacity-60" : ""`} >

        {/* Header — Now Dynamic ✅ */}
        <Card>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-slate-100 grid place-items-center font-semibold text-slate-600">
                {member?.identity?.fullName?.slice(0, 2)?.toUpperCase() || "--"}
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">{member?.identity?.fullName || "—"}</h3>
                <p className="text-sm text-slate-500">{member?.email || "—"}</p>
              </div>
            </div>
            <div className="flex gap-5">
              <button
                onClick={() => setOpenNoteDialog(true)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition
                bg-dark-grey-background
                  text-sm font-medium
                  text-black
                `}
              >
                Add Note
              </button>
              <button
                onClick={handleGoToUser}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition
                bg-dark-grey-background
                  text-sm font-medium
                  text-black
                `}
              >
                Sign in to member
              </button>
              <button
                onClick={handleToggleAccount}
                disabled={isTogglingUser}
                className={`
                px-4 py-2 rounded-lg text-sm font-medium transition
                bg-dark-grey-background
                  text-sm font-medium
                  hover:underline
                  ${isUserDisabled
                                  ? "text-emerald-600 hover:text-emerald-700"
                                  : "text-rose-600 hover:text-rose-700"
                                }
                  disabled:opacity-60
                  disabled:cursor-not-allowed
                `}
              >
                {isTogglingUser
                  ? isUserDisabled
                    ? "Reactivating…"
                    : "Deactivating…"
                  : isUserDisabled
                    ? "Reactivate account"
                    : "Deactivate account"}
              </button>
            </div>

          </div>
        </Card>

        <div className="w-full flex gap-6 items-stretch">
          {/* <div className="lg:col-span-2 space-y-6"> */}

          {/* Recent Reports */}
          <div className="flex-[6]  flex">
            <Card className="h-full flex flex-col">
              <SectionTitle>Recent Reports</SectionTitle>

              {reports.length > 0 && <div className="flex-1 h-full  overflow-y-auto space-y-3 pr-1 min-h-0">
                {reports.map((r) => (
                  <div
                    key={r.report.id}
                    className="
                    flex items-center justify-between
                    rounded-xl border border-slate-200 
                    px-4 py-3 
                    bg-[rgba(249,250,251,1)]
                    transition-all duration-150 ease-out
                    hover:scale-[1.015]
                    hover:bg-slate-100/60
                    cursor-pointer
                    min-w-0
                  "
                    onClick={(e) => {
                      if ((e.target as HTMLElement).tagName === "INPUT") return;
                      const allowedStatuses = ["draft", "ready", "sent", "in_review", "failed", 'outdated'];
                      if (allowedStatuses.includes(r.report.status)) {
                        nav(`/admin/report/${r.report.id}?from=member-view&memberId=${userId}`);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between flex-[4] gap-2 min-w-0">

                      <div className="flex min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate w-full">
                          {r.template?.title || "Untitled Report"}
                        </p>

                      </div>
                    </div>

                    <div className="flex-[2] flex gap-3 items-center">
                      <p className="text-xs text-slate-500 truncate w-full flex gap-1">
                        <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <circle cx="12" cy="12" r="9" /><path d="M12 7v6l4 2" />
                        </svg>
                        {formatDOB(r.report.updatedAt)}
                      </p>

                      {r.report.status ? <Badge
                        className={`${getStatusBadgeClass(
                          (r.report.status).toUpperCase()
                        )} capitalize bold text-xs px-2 py-0.5`}
                      >
                        {r.report.status}
                      </Badge> : r.report.status}
                    </div>

                  </div>

                ))}
              </div>}
              {reports.length == 0 && <div className="text-zinc-900">
                No Reports Data
              </div>}
            </Card>
          </div>


          <div className="flex-[4] min-w-0">
            <MemberDetailsCard data={{ profile: member, mathCalculation: member.mathLogic, }} showRelatedPersonButton={true}
              onRelatedPersonClick={() => setShowRelatedDialog(true)} showMath={true} />
          </div>

          {/* </div> */}
        </div>

        {/* Visits Chart */}
        <Card title="Member Visits Over Time">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <p className="text-xs text-slate-500">Total visits</p>
              <p className="text-3xl font-semibold text-slate-900">
                {visits.reduce((sum, v) => sum + v.value, 0)}
              </p>
            </div>

            <div className="h-56 -mx-5">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={visits.length === 0 ? [{ month: "", year: "", value: 0 }] : visits}
                  margin={{ left: 24, right: 16 }}
                >
                  <defs>
                    <linearGradient id="fill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>

                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(month, index) =>
                      visits[index] ? `${month} ${visits[index].year}` : ""
                    }
                  />

                  <YAxis tickLine={false} axisLine={false} width={28} />

                  <Tooltip
                    formatter={(value: any) => [`${value} visits`, "Visits"]}
                    labelFormatter={(label: any, entry: any) =>
                      entry && entry[0]
                        ? `${entry[0].payload.month} ${entry[0].payload.year}`
                        : ""
                    }
                    cursor={{ stroke: "#94a3b8", strokeDasharray: 4 }}
                  />

                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    fill="url(#fill)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>


        {/* Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Member Detail Section — (Still Static for now) */}

          <Card title="Frequently opened reports">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="text-left px-4 py-2 text-slate-600">Report title</th>
                  <th className="text-right px-4 py-2 text-slate-600"># of views</th>
                </tr>
              </thead>
              <tbody>
                {freqOpened.map((r) => (
                  <tr key={r.title} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-800">{r.title}</td>
                    <td className="px-4 py-3 text-right text-slate-900">{r.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card title="Purchases">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="text-left px-4 py-2 text-slate-600">Report title</th>
                  <th className="text-left px-4 py-2 text-slate-600">Paid</th>
                  <th className="text-left px-4 py-2 text-slate-600">Stripe transaction ID</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r?.report?.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-800 truncate max-w-[25ch]">{r?.template?.title}</td>
                    <td className="px-4 py-3 text-slate-800 flex">$ {r?.report?.amount || "0"}</td>
                    <td className="px-4 py-3 font-mono text-slate-700">{r?.report?.payment?.paymentIntentId || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>

      </div>

      {showRelatedDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div
            className="
      bg-white rounded-xl shadow-xl
      w-full max-w-3xl
      min-h-[60vh]
      max-h-[85vh]
      flex flex-col
    "
          >

            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Related person
              </h2>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {relatedPersonsList.map((rp: any) => {
                const isOpen = openPersonId === rp.id;

                return (
                  <div
                    key={rp.id}
                    className="border border-slate-200 rounded-lg bg-slate-50"
                  >
                    {/* Accordion Header */}
                    <button
                      onClick={() =>
                        setOpenPersonId(isOpen ? null : rp.id)
                      }
                      className="w-full flex items-center justify-between px-4 py-3 text-left"
                    >
                      <span className="font-medium text-slate-900">
                        {rp.identity?.fullName || "—"}
                      </span>

                      <svg
                        className={`h-5 w-5 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""
                          }`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Accordion Body */}
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 text-sm">
                        <div className="grid grid-cols-2 gap-y-3 gap-x-6">

                          <InfoRow label="Other legal names">
                            {rp.legalHistory?.previousNames
                              ?.map((n: any) => n.name)
                              .join(", ") || "—"}
                          </InfoRow>

                          <InfoRow label="Nicknames">
                            {rp.identity?.nicknames?.join(", ") || "—"}
                          </InfoRow>

                          <InfoRow label="Date and time of birth">
                            {rp.birth?.date
                              ? `${formatDOB(rp.birth.date)}, ${rp.birth.time || ""}`
                              : "—"}
                          </InfoRow>

                          <InfoRow label="Dominant hand">
                            {rp.traits?.dominantHand || "—"}
                          </InfoRow>

                          <InfoRow label="Place of birth">
                            {rp.birth?.place || "—"}
                          </InfoRow>

                          <InfoRow label="Current residence">
                            {rp.current?.residence || "—"}
                          </InfoRow>

                          <InfoRow label="Spiritual orientation">
                            {rp.traits?.spiritualOrientation || "—"}
                          </InfoRow>


                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {relatedPersonsList.length === 0 && (
                <div className="text-sm text-slate-600">
                  No related persons available.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setShowRelatedDialog(false)}
                className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <AddEditNoteDialog
        open={openNoteDialog}
        onClose={() => setOpenNoteDialog(false)}
        onSave={()=>{}}
      />
    </div>
    



  );
}
